/**
 * One shared answer to how eagerly the Environment may work when nobody is
 * asking.
 *
 * Every background job keeps its own interval — that is where the job's own
 * judgement lives. The profile only scales it, so a user picks a posture once
 * instead of tuning each job, and a job added later inherits the posture
 * without anyone remembering to wire it up.
 *
 * Scaling rather than replacing matters: a user who lengthened the Git fetch
 * interval on purpose keeps that choice in every profile, stretched or not.
 */
import type { BackgroundActivityProfile } from "@afar-research/contracts/settings";

export interface BackgroundActivityPosture {
  /** Multiplier applied to a job's own interval. */
  readonly intervalScale: number;
  /** Whether background work runs at all while the host reports itself idle. */
  readonly runsWhileIdle: boolean;
}

const POSTURES: Record<BackgroundActivityProfile, BackgroundActivityPosture> = {
  // Twice as eager as balanced, and keeps working while the host is idle.
  performance: { intervalScale: 0.5, runsWhileIdle: true },
  balanced: { intervalScale: 1, runsWhileIdle: true },
  // Four times as patient, and stops entirely once the host goes idle: the
  // point of this profile is that a laptop on battery is left alone.
  "battery-saver": { intervalScale: 4, runsWhileIdle: false },
};

export function backgroundActivityPosture(
  profile: BackgroundActivityProfile,
): BackgroundActivityPosture {
  return POSTURES[profile];
}

/**
 * A job's interval under the current profile, in milliseconds. A job that
 * asks for no delay is asking to run continuously and is left alone; nothing
 * is ever scaled below a second, so no profile can turn an interval into a
 * busy loop.
 */
export function backgroundIntervalMs(input: {
  readonly baseMs: number;
  readonly profile: BackgroundActivityProfile;
}): number {
  if (input.baseMs <= 0) return input.baseMs;
  const scaled = input.baseMs * backgroundActivityPosture(input.profile).intervalScale;
  return Math.max(1_000, Math.round(scaled));
}

/** Whether a background job may run right now. */
export function backgroundActivityAllowed(input: {
  readonly profile: BackgroundActivityProfile;
  readonly hostIdle: boolean;
}): boolean {
  return !input.hostIdle || backgroundActivityPosture(input.profile).runsWhileIdle;
}

export const BACKGROUND_ACTIVITY_PROFILE_LABELS: Record<BackgroundActivityProfile, string> = {
  balanced: "Balanced",
  performance: "Performance",
  "battery-saver": "Battery saver",
};

export const BACKGROUND_ACTIVITY_PROFILE_DESCRIPTIONS: Record<BackgroundActivityProfile, string> = {
  balanced: "Background work runs at each job's own pace.",
  performance: "Background work runs twice as often, and keeps going while the host is idle.",
  "battery-saver": "Background work runs a quarter as often, and stops while the host is idle.",
};
